import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export type ChallengeState = "locked" | "not_started" | "submitted" | "reviewed";

export interface ChallengeCriterion { id: string; name: string; min_points: number; max_points: number; sort_order: number; }

export interface ChallengeItem {
  focusAreaId: string;
  focusAreaName: string;
  number: string;
  moduleId: string | null;
  title: string;
  description: string | null;
  brief: string | null;
  assignmentId: string | null;
  criteria: ChallengeCriterion[];
  state: ChallengeState;
  areaMax: number;
  areaScore: number | null;
  pointsByCriterion: Record<string, number>;
}

export interface TopShopProgramInfo {
  id: string;
  course_id: string | null;
  cohort_id: string | null;
  leaderboard_visible: boolean;
  scores_visible: boolean;
}

/** Program settings for the top-shop program. */
export function useTopShopProgram() {
  return useQuery({
    queryKey: ["top-shop", "program-info"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("id, course_id, cohort_id, leaderboard_visible, scores_visible")
        .eq("slug", "top-shop")
        .maybeSingle();
      if (error) throw error;
      return data as TopShopProgramInfo | null;
    },
  });
}

/** Challenges (one per active focus area) with the signed-in user's progress and scores. */
export function useTopShopChallenges(program: TopShopProgramInfo | null | undefined, applicationId: string | undefined, enabled: boolean) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["top-shop", "challenges", user?.id, program?.id, applicationId],
    enabled: enabled && !!user?.id && !!program?.id,
    queryFn: async (): Promise<ChallengeItem[]> => {
      const { data: areas, error: aErr } = await supabase
        .from("program_focus_areas")
        .select("id, name, sort_order, module_id")
        .eq("program_id", program!.id)
        .eq("is_active", true)
        .order("sort_order");
      if (aErr) throw aErr;
      const areaIds = (areas ?? []).map((a) => a.id);
      const moduleIds = (areas ?? []).map((a) => a.module_id).filter(Boolean) as string[];

      const [mods, content, asg, crit] = await Promise.all([
        moduleIds.length ? supabase.from("modules").select("id, title, description").in("id", moduleIds) : Promise.resolve({ data: [], error: null }),
        moduleIds.length ? supabase.from("module_content").select("module_id, content_text, sequence_order").in("module_id", moduleIds).eq("content_type", "text").order("sequence_order") : Promise.resolve({ data: [], error: null }),
        moduleIds.length ? supabase.from("assignments").select("id, module_id").in("module_id", moduleIds).eq("is_published", true).order("created_at") : Promise.resolve({ data: [], error: null }),
        areaIds.length ? supabase.from("program_challenge_criteria").select("id, focus_area_id, name, min_points, max_points, sort_order").in("focus_area_id", areaIds).eq("is_active", true).order("sort_order") : Promise.resolve({ data: [], error: null }),
      ]);
      for (const r of [mods, content, asg, crit]) if (r.error) throw r.error;

      const assignmentIds = ((asg.data ?? []) as any[]).map((a) => a.id);
      const [subs, enr, access, scores] = await Promise.all([
        assignmentIds.length ? supabase.from("assignment_submissions").select("assignment_id, status").eq("user_id", user!.id).in("assignment_id", assignmentIds) : Promise.resolve({ data: [], error: null }),
        program!.course_id ? supabase.from("course_enrollments").select("gating_enforced, cohort_id").eq("user_id", user!.id).eq("course_id", program!.course_id).maybeSingle() : Promise.resolve({ data: null, error: null }),
        program!.cohort_id && moduleIds.length ? supabase.from("cohort_module_access").select("module_id, unlocked_at").eq("cohort_id", program!.cohort_id).in("module_id", moduleIds) : Promise.resolve({ data: [], error: null }),
        program!.scores_visible && applicationId ? supabase.from("program_criterion_scores").select("criterion_id, points").eq("application_id", applicationId) : Promise.resolve({ data: [], error: null }),
      ]);
      for (const r of [subs, enr, access, scores]) if (r.error) throw r.error;

      const modMap = new Map(((mods.data ?? []) as any[]).map((m) => [m.id, m]));
      const briefMap = new Map<string, string>();
      ((content.data ?? []) as any[]).forEach((c) => { if (!briefMap.has(c.module_id) && c.content_text) briefMap.set(c.module_id, c.content_text); });
      const asgMap = new Map<string, string>();
      ((asg.data ?? []) as any[]).forEach((a) => { if (!asgMap.has(a.module_id)) asgMap.set(a.module_id, a.id); });
      const subMap = new Map(((subs.data ?? []) as any[]).map((s) => [s.assignment_id, s.status as string]));
      const accessMap = new Map(((access.data ?? []) as any[]).map((a) => [a.module_id, a.unlocked_at as string | null]));
      const scoreMap: Record<string, number> = {};
      ((scores.data ?? []) as any[]).forEach((s) => { scoreMap[s.criterion_id] = Number(s.points); });
      const enrollment = enr.data as { gating_enforced: boolean } | null;

      return (areas ?? []).map((a, idx) => {
        const mod = a.module_id ? modMap.get(a.module_id) : null;
        const assignmentId = a.module_id ? asgMap.get(a.module_id) ?? null : null;
        const criteria = ((crit.data ?? []) as any[]).filter((c) => c.focus_area_id === a.id) as ChallengeCriterion[];
        const gated = !!enrollment?.gating_enforced && !!a.module_id && !accessMap.get(a.module_id);
        const sub = assignmentId ? subMap.get(assignmentId) : undefined;
        let state: ChallengeState = "not_started";
        if (gated) state = "locked";
        else if (sub === "passed" || sub === "failed") state = "reviewed";
        else if (sub) state = "submitted";
        const scored = criteria.filter((c) => c.id in scoreMap);
        return {
          focusAreaId: a.id,
          focusAreaName: a.name,
          number: String(idx + 1).padStart(2, "0"),
          moduleId: a.module_id,
          title: mod?.title ?? a.name,
          description: mod?.description ?? null,
          brief: a.module_id ? briefMap.get(a.module_id) ?? null : null,
          assignmentId,
          criteria,
          state,
          areaMax: criteria.reduce((s, c) => s + c.max_points, 0),
          areaScore: program!.scores_visible ? scored.reduce((s, c) => s + scoreMap[c.id], 0) : null,
          pointsByCriterion: scoreMap,
        };
      });
    },
  });
}

export interface LeaderboardData {
  communities: { community: string; parish: string | null; participants: number; average_score: number; total_score: number; rank: number }[];
  individuals: { full_name: string | null; business_name: string; community: string | null; total_score: number; rank: number }[] | null;
}

export function useTopShopLeaderboard(enabled: boolean) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["top-shop", "leaderboard", user?.id],
    enabled: enabled && !!user?.id,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_top_shop_leaderboard" as any);
      if (error) throw error;
      return data as unknown as LeaderboardData;
    },
  });
}
